import React, { useCallback, useMemo } from 'react';
import { Button, Grid, Menu, MenuItem, Paper, Typography } from '@mui/material';
import { DefaultTheme, makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage } from 'react-intl';
import { usePutAnalyticsMetaData } from 'modules/Insights/Notes/hooks/usePutAnalyticsMetaData';
import { AnalyticsMetadataType } from 'graphql/types';
import LabelChartSkeleton from '../LabelChartSkeleton';
import OEFChart from './OEFChart';
import {
  AnalyticsMetaDataOEF,
  useOEFChartContext
} from './OEFChartContextProvider';
const useStyles = makeStyles((theme: DefaultTheme) => ({
  container: {
    padding: theme.spacing(2),
    maxHeight: theme.spacing(32.5),
    minHeight: theme.spacing(32.5),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  btnContainer: {
    width: '100%',
    height: theme.spacing(28.5)
  },
  menuStyle: {
    maxHeight: theme.spacing(20),
    minWidth: theme.spacing(15)
  }
}));

type MenuItemProps = {
  onClick: (labelId: string) => void;
  label: AnalyticsMetaDataOEF;
};

const CustomMenuItem: React.FC<MenuItemProps> = ({
  onClick,
  label
}: MenuItemProps) => {
  const handleClick = useCallback(() => {
    onClick(label.id);
  }, [label, onClick]);
  return (
    <MenuItem onClick={handleClick} data-testid={label.name + label.id}>
      <Typography>{label.name}</Typography>
    </MenuItem>
  );
};

const OEFChartContainer: React.FC = () => {
  const classes = useStyles();
  const {
    isLoading: labelLoading,
    oefLabels,
    savedOefLabels,
    hasMore,
    isEmpty
  } = useOEFChartContext();

  const { putAnalyticsMetaData } = usePutAnalyticsMetaData();

  // add new oef chart
  const unOptedOefs = useMemo(() => {
    return oefLabels.filter(
      (i) => !([...savedOefLabels.map((x) => x.id)].indexOf(i.id) > -1)
    );
  }, [oefLabels, savedOefLabels]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuProps = useMemo(
    () => ({
      paper: classes.menuStyle
    }),
    [classes.menuStyle]
  );

  const updateOEFLabels = useCallback(
    (oefs: AnalyticsMetaDataOEF[]) => {
      putAnalyticsMetaData({
        metadata: oefs,
        type: AnalyticsMetadataType.MetadataOefList
      });
    },
    [putAnalyticsMetaData]
  );

  const handleAddChart = useCallback(
    (labelId: string) => {
      const selectedOefLabel = oefLabels.find((x) => x.id === labelId);
      if (selectedOefLabel) {
        const newLabels = [...savedOefLabels, selectedOefLabel];
        updateOEFLabels(newLabels);
        handleClose();
      }
    },
    [oefLabels, savedOefLabels, updateOEFLabels]
  );

  const handleRemove = useCallback(
    (labelId: string) => {
      const newLabels = savedOefLabels.filter((x) => x.id !== labelId);
      updateOEFLabels(newLabels);
    },
    [savedOefLabels, updateOEFLabels]
  );

  return (
    <>
      {labelLoading ? (
        <Grid item xs={4}>
          <Paper
            elevation={3}
            data-testid={'oef-chart-conatiner-skeleton'}
            className={classes.container}
          >
            <LabelChartSkeleton />
          </Paper>
        </Grid>
      ) : isEmpty ? (
        <></>
      ) : (
        <>
          {savedOefLabels.map((i) => (
            <Grid item xs={4} key={i.id}>
              <OEFChart
                labelName={i.name}
                labelId={i.id}
                handleRemove={handleRemove}
              />
            </Grid>
          ))}
          {hasMore && (
            <Grid item xs={4}>
              <Paper
                elevation={3}
                data-testid={'add-oef-chart'}
                className={classes.container}
              >
                <Grid
                  container
                  justifyContent={'center'}
                  alignItems={'center'}
                  className={classes.btnContainer}
                >
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    data-testid={'add-oef-chart-btn'}
                    aria-controls={open ? 'oef-list-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                  >
                    <FormattedMessage id="insights.timeSheetLabels.addOEFChart" />
                  </Button>
                  <Menu
                    data-testid={'oef-add-chart-dropdown'}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'oef-add-chart-dropdown'
                    }}
                    classes={menuProps}
                  >
                    {unOptedOefs.map((o) => (
                      <CustomMenuItem
                        onClick={handleAddChart}
                        label={o}
                        key={o.id}
                      />
                    ))}
                  </Menu>
                </Grid>
              </Paper>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(OEFChartContainer);
